export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: any,
  ) {
    super(message);
  }
  get isPlanLimit() {
    return this.code === "plan_limit" || this.code === "insufficient_credits";
  }
}

let currentOrgId: string | null = null;
export function setApiOrg(id: string | null) {
  currentOrgId = id;
}
export function getApiOrg() {
  return currentOrgId;
}

type Opts = { body?: unknown; signal?: AbortSignal; headers?: Record<string, string> };

async function request<T>(method: string, path: string, opts: Opts = {}): Promise<T> {
  const headers: Record<string, string> = { ...(opts.headers ?? {}) };
  if (currentOrgId) headers["x-org-id"] = currentOrgId;
  let body: BodyInit | undefined;
  if (opts.body instanceof FormData) body = opts.body;
  else if (opts.body !== undefined) {
    body = JSON.stringify(opts.body);
    headers["Content-Type"] = "application/json";
  }
  const res = await fetch(`/api/v1${path}`, { method, headers, body, credentials: "include", signal: opts.signal });
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  const data = text ? (JSON.parse(text) as any) : null;
  if (!res.ok) {
    const err = data?.error ?? {};
    throw new ApiError(res.status, err.code ?? "error", err.message ?? res.statusText, err.details);
  }
  return data as T;
}

export const api = {
  get: <T>(path: string, opts?: Opts) => request<T>("GET", path, opts),
  post: <T>(path: string, body?: unknown, opts?: Opts) => request<T>("POST", path, { ...opts, body }),
  put: <T>(path: string, body?: unknown, opts?: Opts) => request<T>("PUT", path, { ...opts, body }),
  patch: <T>(path: string, body?: unknown, opts?: Opts) => request<T>("PATCH", path, { ...opts, body }),
  del: <T>(path: string, body?: unknown, opts?: Opts) => request<T>("DELETE", path, { ...opts, body }),
};

export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return "Something went wrong";
}

/** For URLs loaded by the browser directly (img, download links, EventSource) which cannot send headers. */
export function withOrg(url: string): string {
  if (!currentOrgId) return url;
  return `${url}${url.includes("?") ? "&" : "?"}orgId=${encodeURIComponent(currentOrgId)}`;
}
